import { ALL_ITEMS_PAGE_SIZE, PaginatedParams } from 'core/http/pagination';
import { ListProductFunction } from 'hooks/useProducts';
import { cloneDeep, get, set } from 'lodash';

interface ItemPathMappingBase {
    listParams?: Partial<PaginatedParams> & Record<string, any>;
}

export interface ItemPathMapping extends ItemPathMappingBase {
    path: string;
    parentPath?: never;
    groupBy?: never;
}

export interface GroupedItemPathMapping extends ItemPathMappingBase {
    path: string;
    parentPath: string;
    groupBy: string;
}

export type ItemPathMappings = (ItemPathMapping | GroupedItemPathMapping)[];

interface Props {
    values: any;
    declarationId: string;
    itemPaths: ItemPathMappings;
    listProducts: ListProductFunction;
}

/**
 * Writes the goods items the products endpoints hold into the declaration that is about to be
 * saved. A declaration update replaces the stored items with whatever the payload it is sent
 * carries, while the items themselves are created and updated through their own endpoints - so they
 * are listed rather than taken from the form, which only holds the copy it was loaded with and
 * would delete everything added since.
 */
const listItemsIntoForm = async ({ values, declarationId, itemPaths, listProducts }: Props) => {
    let next = cloneDeep(values);

    for (const { path, listParams, parentPath, groupBy } of itemPaths) {
        const payload = await listProducts(declarationId, { size: ALL_ITEMS_PAGE_SIZE, ...listParams });

        // The list actions report a failure by dispatching an error and resolving with nothing.
        // Taking that for a level with no items would send a payload that deletes every item on it.
        if (!payload?.list) throw new Error('Could not list the goods items of the declaration.');

        const items: any[] = payload.list;

        if (!parentPath) {
            next = set(next, path, items);
            continue;
        }

        const parents: any[] = get(next, parentPath) ?? [];

        next = set(
            next,
            parentPath,
            parents.map((parent) =>
                set(
                    { ...parent },
                    path,
                    items.filter((item) => item[groupBy] === parent?.id)
                )
            )
        );
    }

    return next;
};

export default listItemsIntoForm;
